import { extractErrorMessageString } from '@cloudapps/string-helper';
// Material
import { IconButton, LinearProgress } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Image, PictureAsPdf, Replay } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { filesize } from 'filesize';
import { useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { uploadFile } from '../../../shared/helpers/upload-helpers';
import { EnumAppDocFormat, EnumFileStatus } from '../../redux/scholarship.models';

const useStyles = makeStyles(() =>
  createStyles({
    baseStyle: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px',
      borderWidth: 2,
      borderRadius: 2,
      borderColor: '#5e5e5e',
      borderStyle: 'dashed',
      backgroundColor: '#fafafa',
      color: '#5e5e5e',
      outline: 'none',
      transition: 'border .24s ease-in-out',
      cursor: 'pointer'
    },
    activeStyle: {
      borderColor: '#2196f3'
    },
    acceptStyle: {
      borderColor: '#00e676'
    },
    rejectStyle: {
      borderColor: '#ff1744'
    },
    thumbsContainer: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginTop: 16,
      alignItems: 'center',
      justifyContent: 'center'
    },
    thumb: {
      display: 'inline-flex',
      borderRadius: 2,
      border: '1px solid #eaeaea',
      marginBottom: 8,
      marginRight: 8,
      width: 100,
      height: 100,
      padding: 4,
      boxSizing: 'border-box',
      justifyContent: 'center'
    },
    thumbInner: {
      display: 'flex',
      minWidth: 0,
      overflow: 'hidden'
    },
    img: {
      display: 'block',
      width: 'auto',
      height: '100%'
    }
  })
);

const UploadForm = ({ appId, accept, dataSource, disabled, section, userId, handleFile, appendIdentifier = false, identifier = '' }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState<boolean>(false);
  const [files, setFiles] = useState<File[]>([]);
  const [compError, setCompError] = useState<string | undefined>(undefined);
  const [success, setSuccess] = useState<boolean>(false);

  const handleDrop = (droppedFiles: File[]): void => {
    setFiles(
      droppedFiles.map((file: File) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file)
        })
      )
    );
  };

  const { acceptedFiles, getRootProps, getInputProps, isDragAccept, isDragActive, isDragReject } = useDropzone({
    accept,
    disabled,
    multiple: false,
    onDrop: handleDrop
  });

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks
    files.forEach((f: any) => {
      URL.revokeObjectURL(f.preview);
    });
  }, [files]);

  const dropZoneStyle = { className: classes.baseStyle };
  const style = useMemo(() => {
    dropZoneStyle.className += isDragAccept ? ` ${classes.acceptStyle}` : '';
    dropZoneStyle.className += isDragActive ? ` ${classes.activeStyle}` : '';
    dropZoneStyle.className += isDragReject || !!compError ? ` ${classes.rejectStyle}` : '';

    return { ...dropZoneStyle };
  }, [compError, isDragAccept, isDragActive, isDragReject]);

  const doUpload = async () => {
    for (const f of acceptedFiles) {
      setLoading(true);
      let newFileName = f.name.toString();
      if (appendIdentifier) {
        newFileName = newFileName
          .split('.')
          .map((val: string, index: number) => {
            if (index === 0) {
              return `${val}_${identifier
                .toString()
                .normalize('NFD')
                .replace(/\p{Diacritic}/gu, '')}`;
            } else {
              return val;
            }
          })
          .join('.');
      }
      newFileName = newFileName.replace(/[^0-9A-Za-z.\-_()]/gi, '');
      const extension = newFileName.split('.').pop();

      let filePath = '';
      if (dataSource.signatureId) {
        // a signature file
        filePath = `mtpf/${userId}/${appId}/${section}/${dataSource.id}_${dataSource.signatureId}.${extension}`;
      } else {
        filePath = `mtpf/${userId}/${appId}/${section}/${newFileName}`;
      }

      try {
        const response = await uploadFile(f, filePath, section);
        if (response.status === 200) {
          handleFile(
            {
              docFormat: EnumAppDocFormat.FILE,
              docType: section,
              docStatus: EnumFileStatus.PENDING,
              docContent: undefined,
              filePath: filePath.substring(5)
            },
            section
          );

          setLoading(false);
          setSuccess(true);
          setFiles([]);
        }
      } catch (error) {
        setLoading(false);
        console.log(extractErrorMessageString(error));
        setCompError(extractErrorMessageString(error));
        console.log(error);
      }
    }
  };

  useEffect(() => {
    setCompError(undefined);
    setSuccess(false);
    void doUpload();
  }, [acceptedFiles]);

  useEffect(() => {
    setCompError(undefined);
    setSuccess(false);
  }, [dataSource]);

  const thumbs = files.map((f: any) => (
    <div key={f.name} className={classes.thumbsContainer}>
      <div className={classes.thumb}>
        <div className={classes.thumbInner}>
          <IconButton>{f.type === 'application/pdf' ? <PictureAsPdf /> : <Image />}</IconButton>
        </div>
      </div>
      {f.path} - {filesize(f.size)}
    </div>
  ));

  const renderForm = () => (
    <>
      {loading && <LinearProgress style={{ marginBottom: '2px' }} />}
      <div {...getRootProps(style)}>
        <input {...getInputProps()} />
        {disabled ? (
          <p>Delete the existing file to upload a new file.</p>
        ) : thumbs.length > 0 ? (
          thumbs
        ) : (
          <p>Drop a file here or click to select.</p>
        )}
        {compError && (
          <Alert
            severity="error"
            onClose={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setCompError(undefined);
              setFiles([]);
            }}
          >
            {compError}
            <IconButton
              size={'small'}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void doUpload();
              }}
            >
              <Replay fontSize={'small'} />
            </IconButton>
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            onClose={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSuccess(false);
            }}
          >
            File Uploaded
          </Alert>
        )}
      </div>
    </>
  );

  return <section className="container">{renderForm()}</section>;
};

export default UploadForm;
